import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Dialog from "@/components/ui/Dialog";
import { api, type BillingInterval, type BillingPlan } from "@/lib/api";
import { OPEN_PLANS_EVENT, askMultiple, offeredPlans } from "@/lib/plans";
import { usePlan } from "@/hooks/usePlan";
import { testIds } from "@/testIds";

/** Where the browser goes: a seam for the tests. */
export const billingRedirect = { to: (url: string) => window.location.assign(url) };

/**
 * 4a · Plans (launch redesign, Olivia 14 Oct; plans decided 4 Nov 2026): one column per plan, the same
 * rows in the same order, no "popular" ribbon. Choosing a plan goes straight to Stripe Checkout (4c);
 * a paid plan's own column offers Stripe's portal instead. Ask Lilia shows as a multiple of Free's
 * allowance, never as credits. Until Stripe's keys are installed the API answers 503, and the dialog
 * says payments open soon: nothing was charged. Opened by `openPlans()` or `?plans=1`.
 */
export function PlansDialog() {
  const { t } = useTranslation("documents");
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<BillingInterval>("month");
  const [busy, setBusy] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const { data: mine } = usePlan(open);
  const { data: plans } = useQuery({ queryKey: ["billing-plans"], queryFn: api.billing.plans, enabled: open, staleTime: 300_000 });

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_PLANS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PLANS_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("plans") === "1") setOpen(true);
  }, [location.search]);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    setUnavailable(false);
    const params = new URLSearchParams(location.search);
    if (params.has("plans")) {
      params.delete("plans");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  };

  const { free, paid } = offeredPlans(plans ?? []);
  const columns = [...(free ? [free] : []), ...paid];
  const hasYearly = paid.some((p) => p.yearlyPrice != null);
  const current = mine?.plan.slug ?? "free";
  const onPaidPlan = paid.some((p) => p.slug === current);

  const go = async (action: typeof api.billing.portal, key: string) => {
    setBusy(key);
    try {
      const url = await action();
      if (url) billingRedirect.to(url);
      else setUnavailable(true);
    } catch {
      setUnavailable(true);
    } finally {
      setBusy(null);
    }
  };

  const price = (p: BillingPlan) => {
    if (!p.monthlyPrice) return t("plan.plans.free");
    if (period === "year")
      return p.yearlyPrice != null ? t("plan.plans.perYear", { price: money(p.yearlyPrice) }) : t("plan.plans.monthlyOnly");
    return t("plan.plans.perMonth", { price: money(p.monthlyPrice) });
  };

  const limit = (n: number | undefined) => (n == null || n < 0 ? t("plan.plans.unlimited") : String(n));

  const ask = (p: BillingPlan) => {
    if (p.slug === "free") return t("plan.plans.askFree");
    const times = askMultiple(p.caps.aiCreditsPerMonth, free?.caps.aiCreditsPerMonth);
    return times && times > 1 ? t("plan.plans.askTimes", { times }) : t("plan.plans.askLarger");
  };

  return (
    <Dialog isOpen onClose={close} onRequestClose={close} width={Math.min(260 * Math.max(columns.length, 2) + 64, 860)}>
      <div data-testid={testIds.plan.plansDialog} data-interval={period} className="flex flex-col gap-4 pt-1">
        <div className="flex flex-wrap items-center justify-between gap-3 pr-10">
          <h2 className="text-[17px] font-semibold text-[var(--lilia-text)]">{t("plan.plans.title")}</h2>
          {hasYearly && (
            <div role="radiogroup" aria-label={t("plan.plans.billing")} className="flex rounded-[9px] border border-[var(--lilia-border-strong)] p-0.5">
              {(["month", "year"] as const).map((i) => (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={period === i}
                  data-testid={testIds.plan.plansInterval}
                  data-interval={i}
                  onClick={() => setPeriod(i)}
                  className={`h-7 rounded-[7px] px-3 text-[12.5px] font-medium ${period === i ? "bg-[var(--lilia-primary)] text-[color:var(--lilia-on-primary)]" : "text-[var(--lilia-text-secondary)] hover:bg-[var(--lilia-hover)]"}`}
                >
                  {i === "month" ? t("plan.plans.monthly") : t("plan.plans.yearly")}
                </button>
              ))}
            </div>
          )}
        </div>

        {unavailable && (
          <p data-testid={testIds.plan.plansUnavailable} role="status" className="rounded-[10px] bg-[var(--lilia-hover)] px-3 py-2 text-[13px] text-[var(--lilia-text)]">
            {t("plan.plans.unavailable")}
          </p>
        )}

        {!plans ? (
          <p className="text-[13px] text-[var(--lilia-text-secondary)]">{t("plan.plans.loading")}</p>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(200px, 1fr))` }}>
            {columns.map((p) => {
              const isCurrent = p.slug === current;
              const yearlyMissing = period === "year" && p.monthlyPrice && p.yearlyPrice == null;
              return (
                <section
                  key={p.slug}
                  data-testid={testIds.plan.plansColumn}
                  data-plan={p.slug}
                  data-current={isCurrent}
                  className={`flex flex-col gap-3 rounded-[14px] border px-4 py-4 ${isCurrent ? "border-[var(--lilia-primary)]" : "border-[var(--lilia-border)]"}`}
                >
                  <div>
                    <h3 className="text-[15px] font-semibold text-[var(--lilia-text)]">{p.displayName}</h3>
                    <p className="mt-0.5 text-[13px] text-[var(--lilia-text-secondary)]">{price(p)}</p>
                  </div>
                  <dl className="flex flex-col gap-2 text-[12.5px]">
                    <Row label={t("plan.plans.documents")} value={limit(p.caps.maxDocs)} />
                    <Row label={t("plan.plans.imports")} value={limit(p.caps.maxImportsPerMonth)} />
                    <Row label={t("plan.plans.ask")} value={ask(p)} />
                    <Row label={t("plan.plans.coauthors")} value={p.monthlyPrice ? t("plan.plans.included") : t("plan.plans.viewOnly")} />
                    <Row label={t("plan.plans.exports")} value={t("plan.plans.exportsAll")} />
                  </dl>
                  <div className="mt-auto pt-1">
                    {isCurrent && p.monthlyPrice ? (
                      <button
                        type="button"
                        data-testid={testIds.plan.plansManage}
                        disabled={busy !== null}
                        onClick={() => go(api.billing.portal, "portal")}
                        className="h-9 w-full rounded-[9px] border border-[var(--lilia-border-strong)] text-[13px] font-medium text-[var(--lilia-text)] hover:bg-[var(--lilia-hover)] disabled:opacity-60"
                      >
                        {t("plan.plans.manage")}
                      </button>
                    ) : isCurrent ? (
                      <p className="text-center text-[12.5px] font-medium text-[var(--lilia-text-secondary)]">{t("plan.plans.yourPlan")}</p>
                    ) : p.monthlyPrice && !onPaidPlan ? (
                      <button
                        type="button"
                        data-testid={testIds.plan.plansUpgrade}
                        data-plan={p.slug}
                        disabled={busy !== null || !!yearlyMissing}
                        onClick={() => go(() => api.billing.checkout(p.slug, period), p.slug)}
                        className="h-9 w-full rounded-[9px] bg-[var(--lilia-primary)] text-[13px] font-semibold text-[color:var(--lilia-on-primary)] disabled:opacity-60"
                      >
                        {busy === p.slug ? t("plan.plans.opening") : t("plan.plans.choose", { plan: p.displayName })}
                      </button>
                    ) : null}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <p className="text-[12px] leading-[1.5] text-[var(--lilia-text-secondary)]">
          {onPaidPlan ? t("plan.plans.switchInPortal") : t("plan.plans.footnote")}
        </p>
      </div>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[var(--lilia-text-secondary)]">{label}</dt>
      <dd className="text-right font-medium text-[var(--lilia-text)]">{value}</dd>
    </div>
  );
}

function money(n: number) {
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}
